import type { CronCommandType, CronJobView } from "@agentdash/shared";

export interface ControlState {
  /** A queued/applied pause hasn't shown up in a snapshot yet. */
  pausePending: boolean;
  resumePending: boolean;
  wrapPending: boolean;
  /** The host-agent acked the latest crontab edit as failed. */
  agentError: string | null;
}

const LABELS: Record<Exclude<CronCommandType, "run_now">, string> = {
  pause: "Pause",
  resume: "Resume",
  set_schedule: "Schedule change",
  set_wrapped: "Wrap change",
};

/**
 * Which CronRow controls are waiting on the host-agent, read from the job's
 * latest command (the 5-minute lookback in repo-service).
 *
 * "Pending" means the command hasn't been reflected in a snapshot yet, so it
 * has to exclude a command the agent acked as `failed`: that one will never
 * flip the field it targets, and counting it kept the button disabled on
 * "… pending" until the lookback expired — after which it silently re-enabled,
 * and the operator never learned the edit didn't happen. The failure is
 * surfaced as `agentError` instead — unless the snapshot already shows the
 * target state: a crontab write can land and still be acked `failed` (e.g. the
 * `crontab` exec timing out after the spool copy). run_now is left out:
 * CronRow already badges a failed run.
 */
export function controlState(
  job: Pick<CronJobView, "enabled" | "wrapped" | "latest_command">,
): ControlState {
  const lc = job.latest_command ?? null;
  const live = lc && lc.status !== "failed" ? lc : null;
  const wrapWanted =
    lc?.type === "set_wrapped"
      ? (lc.payload as { wrapped?: unknown }).wrapped
      : undefined;
  const landed =
    (lc?.type === "pause" && !job.enabled) ||
    (lc?.type === "resume" && job.enabled) ||
    (typeof wrapWanted === "boolean" && wrapWanted === job.wrapped);
  return {
    pausePending: live?.type === "pause" && job.enabled,
    resumePending: live?.type === "resume" && !job.enabled,
    wrapPending:
      live?.type === "set_wrapped" &&
      typeof wrapWanted === "boolean" &&
      wrapWanted !== job.wrapped,
    agentError:
      lc?.status === "failed" && lc.type !== "run_now" && !landed
        ? `${LABELS[lc.type]} failed on the host: ${lc.error ?? "(no error message)"}`
        : null,
  };
}
